import React from "react";
import { withStyles, withTheme } from "@material-ui/core";
import { compose } from "recompose";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import MapView, {
  Marker,
  MapControls,
  MapConstant,
  Direction,
  Popup,
} from "../../../../../lib/Map";
import {
  TerminalMarker,
  NextStopComplianceStopMarker,
  NextStopCompliancePickupMarker,
  NextStopComplianceStopAPTMarker,
  NextStopComplianceStopLGMarker,
  NextStopCompliancePriorityMarker,
} from "../../../../../components/mapComponents/Markers";
import { NextStopComplianceStopPopup } from "../../../../../components/mapComponents/Popups";
import { withAppConfig } from "../../../../../appConfigContext";
import { getWayPoints, getLastStop } from "../../../../mapComponents/mapHelper";
import { RouteLegend, MapLegend, SelectRoute } from "./Legend";
import {
  getPlannedStopData,
  getDeviationData,
  getActualStopData,
} from "../NextStopComplianceUtills";
import appConstants from "../../../../../appConstants";
import Tooltip from "../../../../common/StyledToolTip";

const tooltipStyle = (theme) => ({
  tooltip: {
    padding: 0,
  },
});

const StyledToolTip = withStyles(tooltipStyle)(Tooltip);

const styles = (theme) => ({
  anchorSubModulePart: {
    borderRight: `1px solid ${theme.AppBGColor}`,
    height: "100%",
  },
});
class NextStopComplianceMap extends React.Component {
  constructor(props) {
    super(props);
    this.terminalCode = this.props.userData.userProfile.terminal.code;
    this.routeComplianceDataPlanned = null;
    this.actualRouteData = null;
    this.stopMarkerData = null;
    this.routeData = null;
    this.state = {
      selectedStop: null,
      styleName: null,
    };
  }

  onStopClick = (e) => {
    this.setState({ selectedStop: e });
    this.props.onStopClick(e.data.stopSequence);
  };

  onTerminalClick = () => {
    this.props.onStopClick(0);
  };

  getStopColor = (stop) => {
    const theme = this.props.theme;
    if (this.props.stopDetails.stopId === stop.stopId) {
      return {
        color: theme.selectedStopMarkerColor,
        textColor: stop.isDeviation
          ? theme.deviationStopColor
          : theme.iconColor,
      };
    } else if (stop.isDeviation) {
      return {
        color: theme.deviationStopColor,
        textColor: theme.selectedStopMarkerColor,
      };
    } else {
      return {
        color: theme.iconColor,
        textColor: theme.selectedStopMarkerColor,
      };
    }
  };

  getStopMarker(stop) {
    if (stop.data.stopType === appConstants.STOP_TYPE.Pickup) {
      return NextStopCompliancePickupMarker;
    } else if (stop.data.priority) {
      return NextStopCompliancePriorityMarker;
    } else if (stop.data.isLiftGate) {
      return NextStopComplianceStopLGMarker;
    } else if (stop.data.isApt) {
      return NextStopComplianceStopAPTMarker;
    } else {
      return NextStopComplianceStopMarker;
    }
  }

  renderStopMarker = (stop) => {
    const colors = this.getStopColor(stop);
    const StopMarker = this.getStopMarker(stop);
    return (
      <StyledToolTip
        title={
          <NextStopComplianceStopPopup
            theme={this.props.theme.themeName}
            data={stop}
            isOnHover={true}
          ></NextStopComplianceStopPopup>
        }
      >
        <div>
          <StopMarker
            stopSequence={stop.stopSequence}
            textColor={colors.textColor}
            color={colors.color}
          />
        </div>
      </StyledToolTip>
    );
  };

  onMapClicked = () => {
    this.setState({
      selectedStop: null,
    });
  };

  getMapStyleName = (styleName) => {
    if (this.state.styleName !== styleName) {
      this.setState({ styleName: styleName });
    }
  };

  render() {
    const {
      classes,
      theme,
      configJSON,
      stopDetails,
      value,
      routeComplianceList,
      updateNextStopComplianceSelectedRoute,
      routeComplianceData,
    } = this.props;
    const { location: terminalLocation } =
      this.props.userData.userProfile.terminal;
    if (this.props.routeComplianceData && stopDetails) {
      this.routeData = routeComplianceData;
      this.routeComplianceDataPlanned = getPlannedStopData(
        routeComplianceData,
        stopDetails.stopSequence,
        theme.HeaderSelectedText
      );
      const actualRouteColor =
        this.state.styleName === appConstants.MAP_STYLE_NAME.Satellite
          ? theme.satelliteViewRouteColor
          : theme.actualRouteColor;
      this.actualRouteData = getActualStopData(
        routeComplianceData,
        terminalLocation,
        actualRouteColor
      );
      this.stopMarkerData = this.routeData.actualStops.filter(
        (stop) => stop.stopId
      );
    }
    const { selectedStop } = this.state;
    return (
      <div className={classes.anchorSubModulePart}>
        <MapView
          zoom={9}
          initialCenter={terminalLocation}
          center={terminalLocation}
          mapId="NextStopMap"
          mapTheme={theme.themeName}
          mapConfig={configJSON?.maps}
          onClick={this.onMapClicked}
          getMapStyleName={this.getMapStyleName}
        >
          <MapControls
            controlOptions={{
              zoomControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
              satelliteViewControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
              reCenterMapControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
            }}
          />

          <Marker
            position={terminalLocation}
            animation
            anchorPoint={{ x: 20, y: 20 }}
            zIndex={1}
            forceUpdate={true}
            isActiveShipment={true}
            onClick={this.onTerminalClick}
          >
            <TerminalMarker
              data={this.props.userData.userProfile.terminal}
              color={theme.terminalContentColor}
              fillColor={
                this.routeData &&
                this.routeData.actualStops[0].data?.isTerminal &&
                this.routeData.actualStops[0].isDeviation
                  ? theme.deviationStopColor
                  : null
              }
            />
          </Marker>

          {value && this.routeComplianceDataPlanned.plannedStops?.length > 0 ? (
            <Direction
              refreshOnMapStyleLoaded
              origin={
                !this.routeComplianceDataPlanned.data?.isTerminal
                  ? {
                      lat: this.routeComplianceDataPlanned.data.latitude,
                      lng: this.routeComplianceDataPlanned.data.longitude,
                    }
                  : terminalLocation
              }
              destination={getLastStop(
                this.routeComplianceDataPlanned.plannedStops
              )}
              waypoints={getWayPoints(
                this.routeComplianceDataPlanned.plannedStops
              )}
              strokeColor={this.routeComplianceDataPlanned.color}
              data={this.routeComplianceDataPlanned}
              routeAnimation={false}
              key={this.routeComplianceDataPlanned.routeId}
              zIndex={0}
            />
          ) : null}

          {this.actualRouteData && this.actualRouteData.stops.length > 0 ? (
            <Direction
              refreshOnMapStyleLoaded
              origin={terminalLocation}
              destination={getLastStop(this.actualRouteData.stops)}
              waypoints={getWayPoints(this.actualRouteData.stops)}
              strokeColor={this.actualRouteData.color}
              routeAnimation={true}
              data={this.actualRouteData}
              key={this.actualRouteData.routeId}
              zIndex={0}
              pathType="dotted"
            />
          ) : null}

          {this.stopMarkerData?.map((dev) => {
            const stop = getDeviationData(
              routeComplianceData,
              dev.stopSequence,
              this.terminalCode
            );
            return (
              stop && (
                <Marker
                  data={stop}
                  position={{
                    lat: stop.data.latitude,
                    lng: stop.data.longitude,
                  }}
                  key={stop.stopId}
                  zIndex={1}
                  forceUpdate={true}
                  isActiveShipment={false}
                  onClick={this.onStopClick}
                >
                  {this.renderStopMarker(stop)}
                </Marker>
              )
            );
          })}

          {selectedStop &&
            stopDetails &&
            selectedStop.data.stopSequence === stopDetails.stopSequence && (
              <Popup marker={selectedStop.marker}>
                <NextStopComplianceStopPopup
                  theme={theme.themeName}
                  data={selectedStop.data}
                  isOnHover={false}
                ></NextStopComplianceStopPopup>
              </Popup>
            )}

          <MapLegend />
          <RouteLegend
            header="Actual route"
            label="GPS coordinates of the driver"
            type="Actual"
          />
          <RouteLegend
            header="Planned route at stop"
            label="RouteMAX suggested route"
            type="Planned"
          />
          <SelectRoute
            selectedRoute={this.props.nextStopComplianceSelectedRoute}
            routeComplianceList={routeComplianceList}
            updateNextStopComplianceSelectedRoute={
              updateNextStopComplianceSelectedRoute
            }
          />
        </MapView>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  withTheme(),
  withUserProfile,
  withAppConfig
)(NextStopComplianceMap);
